import React, { useState } from 'react';
import { X, CheckCircle, Clock, XCircle, FileText, Edit, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { ResponsiveModal } from '../ui/ResponsiveModal';

interface StatusChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newStatus: string, note: string) => Promise<void>;
  currentStatus: string;
  newStatus: string;
  dealTitle: string;
  noteRequired?: boolean;
}

// Notion-style status colors
const statusOptions = [
  { value: 'draft', label: 'Bozza', icon: Edit, color: 'bg-[rgba(55,53,47,0.08)] text-[#787774]', dotColor: '#787774' },
  { value: 'sent', label: 'Inviata', icon: FileText, color: 'bg-[rgba(35,131,226,0.1)] text-[#2383E2]', dotColor: '#2383E2' },
  { value: 'viewed', label: 'Visualizzata', icon: Clock, color: 'bg-[rgba(203,145,47,0.1)] text-[#CB912F]', dotColor: '#CB912F' },
  { value: 'accepted', label: 'Accettata', icon: CheckCircle, color: 'bg-[rgba(15,123,108,0.1)] text-[#0F7B6C]', dotColor: '#0F7B6C' },
  { value: 'rejected', label: 'Rifiutata', icon: XCircle, color: 'bg-[rgba(212,76,71,0.1)] text-[#D44C47]', dotColor: '#D44C47' },
  { value: 'expired', label: 'Scaduta', icon: AlertCircle, color: 'bg-[rgba(55,53,47,0.08)] text-[#787774]', dotColor: '#787774' },
];

export const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentStatus,
  newStatus,
  dealTitle,
  noteRequired = true
}) => {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const currentStatusInfo = statusOptions.find(s => s.value === currentStatus);
  const newStatusInfo = statusOptions.find(s => s.value === newStatus);
  const CurrentIcon = currentStatusInfo?.icon || Edit;
  const NewIcon = newStatusInfo?.icon || Edit;

  const handleConfirm = async () => {
    if (noteRequired && !note.trim()) return;

    setLoading(true);
    try {
      await onConfirm(newStatus, note.trim());
      setNote('');
      onClose();
    } catch (error) {
      console.error('Error changing status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNote('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ResponsiveModal isOpen={isOpen} onClose={handleClose} title="Cambia Stato Offerta">
      <div className="space-y-5">
        {/* Deal info - Notion style */}
        <div className="bg-[#FBFBFA] dark:bg-gray-800/50 rounded-lg p-4 border border-[rgba(55,53,47,0.09)] dark:border-gray-700">
          <h3 className="font-medium text-[#37352F] dark:text-white mb-3 text-sm">{dealTitle}</h3>

          {/* Status change visualization with dots */}
          <div className="flex items-center gap-3">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium ${currentStatusInfo?.color}`}>
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: currentStatusInfo?.dotColor }}
              />
              {currentStatusInfo?.label}
            </div>

            <div className="text-[#787774] dark:text-gray-500">→</div>

            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium ${newStatusInfo?.color}`}>
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: newStatusInfo?.dotColor }}
              />
              {newStatusInfo?.label}
            </div>
          </div>
        </div>

        {/* Note input - Notion style */}
        <div>
          <label htmlFor="status-note" className="block text-xs font-medium text-[#37352F] dark:text-white mb-1.5">
            {noteRequired ? 'Nota richiesta per il cambio di stato *' : 'Nota (opzionale)'}
          </label>
          <textarea
            id="status-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Inserisci una nota che spieghi il motivo del cambio di stato..."
            className="w-full px-3 py-2 border border-[rgba(55,53,47,0.16)] dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#2383E2] focus:border-[#2383E2] dark:focus:ring-blue-500 dark:focus:border-blue-500 resize-none h-20 text-sm bg-white dark:bg-gray-800 text-[#37352F] dark:text-white placeholder-[#787774] dark:placeholder-gray-500"
            required
          />
          <p className="mt-1 text-[11px] text-[#787774] dark:text-gray-400">
            Questa nota verrà aggiunta alla cronologia del deal.
          </p>
        </div>

        {/* Action buttons - Notion style */}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            className="text-xs h-8 px-3"
          >
            Annulla
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={(noteRequired && !note.trim()) || loading}
            className="bg-[#2383E2] hover:bg-[#1a6bc2] text-white text-xs h-8 px-3"
          >
            {loading ? 'Aggiornamento...' : 'Conferma'}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default StatusChangeModal;